import { useId } from 'react';

import { cn } from '@/lib/cn';

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  id?: string;
  /**
   * The name for a switch whose meaning comes from the row it sits in.
   *
   * A list of switches beside filenames reads perfectly on screen and as
   * "switch, on" nine times over to a screen reader - the label is in the
   * column to the left, which the control does not own. This says it.
   */
  'aria-label'?: string;
}

/**
 * A two-state toggle built on `role="switch"` rather than a styled checkbox,
 * so assistive tech announces "on"/"off" instead of "checked".
 */
export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
  id,
  'aria-label': ariaLabel,
}: SwitchProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const descriptionId = description ? `${controlId}-description` : undefined;

  const control = (
    <button
      id={controlId}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-describedby={descriptionId}
      disabled={disabled}
      onClick={() => {
        onChange(!checked);
      }}
      className={cn(
        'relative inline-flex h-4.5 w-8 shrink-0 items-center rounded-full border transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'border-primary bg-primary' : 'border-border-strong bg-surface-muted',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'h-3 w-3 rounded-full bg-surface shadow-sm transition-transform',
          checked ? 'translate-x-4.5' : 'translate-x-0.5',
        )}
      />
    </button>
  );

  if (!label) {
    return control;
  }

  return (
    <div className="flex items-start gap-2.5">
      {control}
      <div className="min-w-0">
        <label
          htmlFor={controlId}
          className={cn('block text-sm text-content', disabled && 'opacity-60')}
        >
          {label}
        </label>
        {description && (
          <p id={descriptionId} className="mt-0.5 text-xs text-content-muted">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}
